import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Section } from "../components/SectionTitle";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { Calendar, ArrowLeft, Tag } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/blog/posts/${slug}`)
      .then((r) => { if (!cancelled) setPost(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  const jsonLd = useMemo(() => post && ({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.created_at,
    author: { "@type": "Organization", name: post.author || "HSA Service Hub Editorial" },
    publisher: { "@type": "Organization", name: "HSA Service Hub" },
  }), [post]);

  if (notFound) {
    return (
      <Section>
        <div className="text-center py-20">
          <h2 className="font-serif-hsa text-3xl text-[#0a2540] mb-4">Article not found</h2>
          <Link to="/blog"><Button>Back to Blog</Button></Link>
        </div>
      </Section>
    );
  }
  if (!post) return <div className="py-32 text-center text-slate-500">Loading...</div>;

  return (
    <div>
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, ${post.title}`}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        jsonLd={jsonLd}
      />
      <article className="max-w-3xl mx-auto px-6 lg:px-0 pt-16 pb-24">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-[#1e6dd8] hover:text-[#0a2540] mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to all articles
        </Link>

        <div className="eyebrow mb-3 inline-flex items-center gap-2">
          <Tag className="w-3.5 h-3.5" /> {post.category}
        </div>
        <h1 className="font-serif-hsa text-4xl sm:text-5xl font-bold text-[#0a2540] leading-[1.1] mb-5">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-8 pb-8 border-b border-[#cbd5e1]">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />
            {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span>By {post.author || "HSA Service Hub Editorial"}</span>
        </div>

        {post.cover_image && (
          <div className="rounded-2xl overflow-hidden mb-10 border border-[#cbd5e1]">
            <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
          </div>
        )}

        <div className="prose prose-slate max-w-none text-slate-700 leading-[1.85] text-lg whitespace-pre-line">
          {post.content}
        </div>

        <div className="mt-14 p-8 bg-[#f8f9fa] border border-[#cbd5e1] rounded-2xl text-center">
          <h3 className="font-serif-hsa text-2xl font-semibold text-[#0a2540] mb-3">Have questions about this topic?</h3>
          <p className="text-slate-600 mb-6">Book a free consultation with an HSA Service Hub advisor.</p>
          <Link to="/contact">
            <Button data-testid="post-cta-btn" className="bg-[#0a2540] hover:bg-[#1e6dd8] text-white rounded-full px-7 h-12">
              Book a Consultation
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
